/* eslint-disable react/prop-types */
import React from 'react';

/**
 * Sub-Batch 3M (Niranjan, Feb 26 2026) — extracted from BreedingPipeline.js.
 * Pure visual extraction: zero behavioural change.  Original JSX block was
 * lines ~358-423 of BreedingPipeline.js.
 *
 * Renders the four sub-blocks of the breeding-recommendations panel:
 *   • Top Genotypes to Advance
 *   • Genotypes to Reject
 *   • Selection Criteria
 *   • Recommended Strategy
 *   • Expected Genetic Gain
 *
 * All formatting and conditional-rendering logic is preserved verbatim.
 */

const _sf = (v, d = 2) =>
  (typeof v === 'number' ? v.toFixed(d) : String(v ?? '-'));

export const RecommendationsSection = ({ recommendations }) => {
  if (!recommendations) return null;
  return (
    <div
      className="space-y-3"
      data-testid="pipeline-recommendations"
    >
      <h3
        className="font-body text-sm font-bold"
        style={{ color: 'var(--green-900)' }}
      >
        Breeding Recommendations
      </h3>

      {/* Top Advance / Bottom Reject cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {recommendations.top_advance?.length > 0 && (
          <div
            className="rounded-xl p-4"
            style={{ background: '#E8F5E9', border: '1px solid #A5D6A7' }}
          >
            <p
              className="font-body text-xs font-bold mb-2"
              style={{ color: '#1B5E20' }}
            >
              Top Genotypes to Advance
            </p>
            {recommendations.top_advance.map((g, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1 border-b border-white/50"
              >
                <span
                  className="font-body text-sm font-bold"
                  style={{ color: '#1B4332' }}
                >
                  {g.name}
                </span>
                <span className="font-mono text-xs" style={{ color: '#2E7D32' }}>
                  Yield={_sf(g.yield)} Index={_sf(g.index)}
                </span>
              </div>
            ))}
          </div>
        )}
        {recommendations.bottom_reject?.length > 0 && (
          <div
            className="rounded-xl p-4"
            style={{ background: '#FFEBEE', border: '1px solid #EF9A9A' }}
          >
            <p
              className="font-body text-xs font-bold mb-2"
              style={{ color: '#B71C1C' }}
            >
              Genotypes to Reject
            </p>
            {recommendations.bottom_reject.map((g, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1 border-b border-white/50"
              >
                <span
                  className="font-body text-sm font-bold"
                  style={{ color: '#C62828' }}
                >
                  {g.name}
                </span>
                <span className="font-mono text-xs" style={{ color: '#E53935' }}>
                  Yield={_sf(g.yield)} Index={_sf(g.index)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selection Criteria + Strategy */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {recommendations.selection_criteria?.length > 0 && (
          <div
            className="rounded-xl p-4 bg-white"
            style={{ border: '1px solid var(--border-color)' }}
          >
            <p
              className="font-body text-xs font-bold mb-2"
              style={{ color: 'var(--green-900)' }}
            >
              Best Selection Criteria
            </p>
            {recommendations.selection_criteria.map((sc, i) => (
              <div key={i} className="flex items-center justify-between py-1">
                <span className="font-body text-sm">{sc.trait}</span>
                <span
                  className="font-body text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background:
                      sc.priority === 'High'
                        ? '#E8F5E9'
                        : sc.priority === 'Medium'
                          ? '#FFF3E0'
                          : '#F5F5F5',
                    color:
                      sc.priority === 'High'
                        ? '#1B5E20'
                        : sc.priority === 'Medium'
                          ? '#E65100'
                          : '#666',
                  }}
                >
                  {sc.priority}
                </span>
              </div>
            ))}
          </div>
        )}
        {recommendations.strategy && (
          <div
            className="rounded-xl p-4 bg-white"
            style={{ border: '1px solid var(--border-color)' }}
          >
            <p
              className="font-body text-xs font-bold mb-1"
              style={{ color: 'var(--green-900)' }}
            >
              Recommended Strategy
            </p>
            <p
              className="font-body text-sm font-bold mb-1"
              style={{ color: 'var(--green-700)' }}
            >
              {recommendations.strategy}
            </p>
            <p
              className="font-body text-xs"
              style={{ color: 'var(--text-medium)', lineHeight: 1.5 }}
            >
              {recommendations.strategy_detail}
            </p>
          </div>
        )}
      </div>

      {/* Expected Genetic Gain */}
      {recommendations.expected_gain?.expected_gain && (
        <div
          className="rounded-xl p-4 bg-white"
          style={{ border: '1px solid var(--border-color)' }}
        >
          <p
            className="font-body text-xs font-bold mb-1"
            style={{ color: 'var(--green-900)' }}
          >
            Expected Genetic Gain (top 20% selected)
          </p>
          <p className="font-mono text-sm" style={{ color: 'var(--text-dark)' }}>
            ΔG = h² × S = {recommendations.expected_gain.h2}% ×{' '}
            {recommendations.expected_gain.selection_differential} ={' '}
            <b>{recommendations.expected_gain.expected_gain}</b> (
            {recommendations.expected_gain.gain_pct}% of mean)
          </p>
        </div>
      )}
    </div>
  );
};

export default RecommendationsSection;
